import {
    ref,
    computed,
    onBeforeUpdate,
    Comment,
    Fragment,
    Text,
    useSlots,
    type VNode,
    type Slot,
    type Slots,
    type Ref,
    type ComputedRef,
} from 'vue';

// Credits: https://gist.github.com/marina-mosti/99b08783b161fa4ba21ebd2ec664fa14
// Modified by: Sean Sattler <sattler@portuna.de> Added TypeScript support and changed some logic

function normalizeToVNodeArray(
    vnodeOrArray: VNode | VNode[] | null | undefined,
): VNode[]
{
    if (!vnodeOrArray)
    {
        return [];
    }

    return Array.isArray(vnodeOrArray) ? vnodeOrArray : [vnodeOrArray];
}

function vNodeIsEmpty(vnodeList: VNode[]): boolean
{
    return vnodeList.every((vnode) =>
    {
        if (vnode.type === Comment)
        {
            return true;
        }

        if (vnode.type === Text)
        {
            const children = vnode.children;

            return typeof children === 'string' ? children.trim() === '' : true;
        }

        if (vnode.type === Fragment)
        {
            const children = vnode.children;
            if (Array.isArray(children))
            {
                return vNodeIsEmpty(children as VNode[]);
            }

            return true;
        }

        return false;
    });
}

export const isEmptySlot = (
    slotCandidate: Slot | unknown,
    slotProps?: Record<string, unknown>,
): boolean =>
{
    if (!slotCandidate)
    {
        return true;
    }

    // if we get a slot that is not a function, we're in vue 2 and there is content, so it's not empty
    if (typeof slotCandidate !== 'function')
    {
        return false;
    }

    const renderedContent = slotCandidate(slotProps as any);

    return vNodeIsEmpty(
        normalizeToVNodeArray(renderedContent as VNode | VNode[] | undefined),
    );
};

export default function useEmptySlots(
    targetSlotName: string                                            = 'default',
    scopedSlotPropsInjection: Record<string, Record<string, unknown>> = {},
): {
    emptySlots: Ref<Record<string, boolean>>;
    definedSlotNames: Ref<string[]>;
    targetSlotIsEmpty: ComputedRef<boolean | undefined>;
}
{
    const emptySlots       = ref<Record<string, boolean>>({});
    const definedSlotNames = ref<string[]>([]);

    const checkSlotEmptiness = (): void =>
    {
        const availableSlots: Slots = useSlots();

        definedSlotNames.value = Object.keys(availableSlots);

        const slotEmptinessStatus: Record<string, boolean> = {};
        for (const slotName of definedSlotNames.value)
        {
            const slotFunction = availableSlots[slotName];

            slotEmptinessStatus[slotName] = isEmptySlot(
                slotFunction,
                scopedSlotPropsInjection[slotName],
            );
        }

        emptySlots.value = slotEmptinessStatus;
    };

    onBeforeUpdate(checkSlotEmptiness);

    checkSlotEmptiness();

    const targetSlotIsEmpty = computed<boolean | undefined>(() =>
    {
        if (!targetSlotName)
        {
            return undefined;
        }

        return (!definedSlotNames.value.includes(targetSlotName) || emptySlots.value[targetSlotName]);
    });

    return {
        emptySlots,
        definedSlotNames,
        targetSlotIsEmpty,
    };
}